import { mobileAccess, virtualDir } from "#skyward_global";
import { ButtonIcon, ButtonText, closeButton, ModalDialog, showDialog } from "#skyward_modalLayer";
import { getCurrentPopupId, notifyDOMLoad, registerLoadDOMCallback } from "#skyward_window";
import { rawData } from "./skyward.base.extensions";
import { registerEvents } from "./skyward.ui.events";
import { displayValidationMessages } from "./skyward.ui.validation";
import { CombinedFieldProps, DataFieldProps, FormattedFieldProps, JQueryInput, TreeBrowseProps, TreeBrowseSection, VueReturnType } from "./types";
import { FormattedRelationshipField, isRelationshipField, TreeBrowseList } from "./vue/treebrowse";
import { BreadcrumbContainer } from "./vue/treebrowse/breadcrumbContainer";
import { BreadcrumbData, ServerBreadcrumb, ServerData } from "./vue/treebrowse/types";

interface InitialTreeBrowseData
{
    lookupId: string;
    expandUrl: string;
    renderSecurityDataObjectFieldsOnly: boolean;
    hideFieldsWithAnyRestriction: boolean;
    fieldsOnlyOfType: string | undefined;
    renderEnvironmentVariables: boolean;
    filterEnvironmentVariablesByLicense: boolean;
    absoluteTreeBrowseObjectPath: string;
    rootModule: string;
    rootObject: string;
    showParametersColumn: boolean;
    lookupModule: string;
    lookupObject: string;
    allowOneToManyBrowse: "true" | "false" | boolean;
    maxOneToManyNestedLevels: number | undefined;
    renderFields: boolean;
    renderOneToMany: boolean;
    renderProxies: boolean;
    renderColumnGroups: boolean;
    renderStandardRelationships: boolean;
    standardFilterName: string | undefined;
    standardFilterId: string | undefined;
    renderOperationFields: "true" | "false" | boolean;
    renderRowForImageReferences: boolean;
    browseId: string | undefined;
    seekModule: string | undefined;
    seekObject: string | undefined;
    seekFieldPath: string | undefined;
    seekPath: string | undefined;
    seekRelationship: string | undefined;
    renderSuperUserColumn: boolean;
    additionalFilterTypeName: string | undefined;
}

export interface TreeBrowseData extends InitialTreeBrowseData
{
    vueList: VueReturnType<typeof TreeBrowseList>;
    breadcrumbContainer: VueReturnType<typeof BreadcrumbContainer>;

    mainContainer: JQuery<HTMLElement>;
    treeBrowseList: JQuery<HTMLElement>;
    parentSectionDiv: JQuery<HTMLElement>;
    fieldSearchBar: JQuery<HTMLElement>;
    fieldSearchButton: JQuery<HTMLElement>;

    selectedRecord: JQuery<HTMLElement>;
    isDataField: boolean;
    isOneToMany: boolean;
    isCalculatedField: boolean;

    selectRecord(fieldKey?: string | null): boolean;
    selectFirstRecord(): void;
    clearSelectedRecord(): void;
    getSelectedFieldData(): FormattedFieldProps | undefined;

    breadcrumbs: Array<ServerBreadcrumb>;

    getCurrentBreadcrumb(): ServerBreadcrumb;
    getExistingBreadcrumbIndex(path: String | undefined): number;

    replaceTreeBrowseData(module: string, object: string, positionObjectPath: string, customUrl: string | null, displayName: string | null, breadcrumbsLeadingBackToRoot: ServerBreadcrumb[], rootModule: string, rootObject: string, absoluteTreeBrowseObjectPath: string, updateParentSectionCombobox?: boolean, replaceData?: boolean): JQueryPromise<void>;
    refresh(): JQueryPromise<void>;

    // TODO define seekToField's type
    seekToField(rowData: any): void;
    getFieldData(element: JQueryInput): FormattedFieldProps | undefined;
    getFieldElement(element: string): HTMLElement | undefined;

    additionalSections: TreeBrowseSection[];
    onRowMounted: ((rowElement: HTMLElement) => void) | undefined;
}

function isInitializedTreeBrowseData(data: InitialTreeBrowseData): data is TreeBrowseData
{
    return !!(data as TreeBrowseData).vueList;
}

const skyward = window.skyward;
skyward.treebrowse = skyward.treebrowse || {};

registerLoadDOMCallback(init);

registerEvents("treebrowse",
    {
        editSuperUserFieldVisibility
    });

export function getTreeBrowseContainer(controlName: string): JQuery<HTMLElement>
{
    let treeBrowse = $(`#${controlName}`);
    if (treeBrowse.length === 0)
    {
        treeBrowse = $(`.treeBrowse[data-lookup-id='${controlName}']`);
    }
    return treeBrowse;
}

export function setBrowseTarget(module: string, object: string, fieldPath: string, controlName: string, customUrl: string | null, displayName: string,
    hideSearchButton: boolean, rootModule: string, rootObject: string, absoluteTreeBrowseObjectPath: string, updateParentSectionCombobox: boolean)
{
    var treeBrowseData = getTreeBrowseContainer(controlName).data() as TreeBrowseData;
    if (typeof treeBrowseData.replaceTreeBrowseData === 'function')
    {
        let rootBreadcrumb = {
            moduleName: module,
            objectName: object,
            displayName: displayName,
            positionObjectPath: fieldPath,
            isOneToMany: false
        };

        treeBrowseData.fieldSearchButton.data("startModule", module);
        treeBrowseData.fieldSearchButton.data("startObject", object);
        treeBrowseData.fieldSearchButton.data("absoluteTreeBrowseObjectPath", absoluteTreeBrowseObjectPath);

        var replacingTreeBrowseDataPromise = treeBrowseData.replaceTreeBrowseData(module, object, fieldPath, customUrl,
            displayName, [rootBreadcrumb], rootModule, rootObject, absoluteTreeBrowseObjectPath, updateParentSectionCombobox);
        if (hideSearchButton)
        {
            replacingTreeBrowseDataPromise.then(function ()
            {
                treeBrowseData.fieldSearchBar.hide();
            });
        }
        else
        {
            treeBrowseData.fieldSearchBar.show();
        }

        return replacingTreeBrowseDataPromise;
    }
    return undefined;
}

export function seekToField(controlToUpdate: string, data: unknown)
{
    const treeBrowse = getTreeBrowseContainer(controlToUpdate);
    if (treeBrowse.length)
    {
        const treeBrowseData = treeBrowse.data() as TreeBrowseData | undefined;
        if (!treeBrowseData)
        {
            throw new ReferenceError(`${controlToUpdate} is not a treebrowse`);
        }
        if (data !== undefined)
        {
            treeBrowseData.seekToField(data);
        }
        else
        {
            treeBrowseData.selectFirstRecord();
        }
    }
}

export function getTreeBrowseData(treeBrowseContainer: JQueryInput): TreeBrowseData
{
    var treeBrowse: JQuery = $(treeBrowseContainer);
    if (!treeBrowse.is(".treeBrowse.treeBrowseContainer"))
    {
        treeBrowse = treeBrowse.find(".treeBrowse.treeBrowseContainer").first();
    }
    return loadTreeBrowseData(treeBrowse);
}

export function getTreeBrowseDataByName(controlName: string): TreeBrowseData
{
    return getTreeBrowseData(getTreeBrowseContainer(controlName)) as TreeBrowseData;
}

/**
* @param {JQuery|Element|string} treeBrowseContainer
*/
export function getSelectedFieldData(treeBrowseContainer: JQueryInput): FormattedFieldProps | undefined
{
    var data = getTreeBrowseData(treeBrowseContainer) as TreeBrowseData | undefined;
    return data?.getSelectedFieldData();
}

export function clearSelectedRecord(treeBrowseContainer: JQueryInput): void
{
    let data = getTreeBrowseData(treeBrowseContainer) as TreeBrowseData | undefined;
    return data?.clearSelectedRecord();
}

function init(selector: string)
{
    $(selector).find(".treeBrowse.treeBrowseContainer").each(function ()
    {
        const treeBrowse = $(this);

        instantiateTreeBrowse(treeBrowse);
    });
}

function loadTreeBrowseData(treeBrowse: JQuery<HTMLElement>)
{
    // Create closure scope to wrap instance vars (treeBrowse and treeBrowseData remain locally in-scope, too)
    const initialTreeBrowseData = treeBrowse.data() as InitialTreeBrowseData;
    if (isInitializedTreeBrowseData(initialTreeBrowseData))
    {
        return initialTreeBrowseData;
    }
    const treeBrowseData = initialTreeBrowseData as TreeBrowseData;
    // $.data({ obj }) does not replace the data object, but only performs a shallow copy.  We have to
    // define these getters directly on the returned object in order for them to stick.
    Object.defineProperties(treeBrowseData,
        {
            selectedRecord:
            {
                get: function (this: TreeBrowseData)
                {
                    var selectedElement = this.vueList.getSelectedElement();
                    return selectedElement ? $(selectedElement) : $();
                }
            },
            isDataField:
            {
                get: function (this: TreeBrowseData)
                {
                    const selectedData = this.vueList.getSelectedFieldData() as FormattedFieldProps | undefined;
                    return selectedData !== undefined && !selectedData.isRelationship && !selectedData.isCalculatedField;
                }
            },
            isCalculatedField:
            {
                get: function (this: TreeBrowseData)
                {
                    const selectedData = this.vueList.getSelectedFieldData() as FormattedFieldProps | undefined;
                    return selectedData?.isCalculatedField;
                }
            },
            isOneToMany:
            {
                get: function (this: TreeBrowseData)
                {
                    const selectedData = this.vueList.getSelectedFieldData();
                    return selectedData && isRelationshipField(selectedData) && selectedData.relationshipType === "OneToMany";
                }
            },
            additionalSections:
            {
                get: function (this: TreeBrowseData)
                {
                    return this.vueList.additionalSections;
                },
                set: function (this: TreeBrowseData, value: TreeBrowseSection[])
                {
                    this.vueList.additionalSections = value;
                }
            },
            onRowMounted:
            {
                get: function (this: TreeBrowseData)
                {
                    return this.vueList.onRowMounted;
                },
                set: function (this: TreeBrowseData, value: (rowElement: HTMLElement) => void)
                {
                    this.vueList.onRowMounted = value;
                }
            },
            breadcrumbs: {
                get: function (this: TreeBrowseData)
                {
                    return this.breadcrumbContainer.breadcrumbs;
                },
                set: function (this: TreeBrowseData, value: Array<ServerBreadcrumb>)
                {
                    this.breadcrumbContainer.breadcrumbs = value;
                }
            }
        });


    return treeBrowseData;
}

function instantiateTreeBrowse(treeBrowse: JQuery<HTMLElement>)
{
    var treeBrowseData = loadTreeBrowseData(treeBrowse);

    var controlName = treeBrowseData.lookupId;
    var fullExpandUrl = virtualDir + treeBrowseData.expandUrl;
    treeBrowseData.mainContainer = treeBrowse;
    treeBrowseData.mainContainer.css('height', '100%');
    treeBrowseData.treeBrowseList = treeBrowse.find(".treebrowse__treeGridDiv");
    var insideOneToMany = false;

    // formerly called "treeGridDiv", this trigger needs to be uncoupled from browse.ts and reporting.js
    var treeBrowseTriggerBindingPoint = treeBrowseData.treeBrowseList;
    var routeHashData = getRouteHashData();

    let isMounted = false;
    const treeBrowseContainer = $("<table />");
    // used by onRecordDoubleClick listener and set in replaceList()
    let oneToManyAllowed = false;
    const treeBrowseProps: TreeBrowseProps =
    {
        sections: [],
        additionalSections: [],
        showParametersColumn: treeBrowseData.showParametersColumn,
        showEyeColumn: treeBrowseData.renderSuperUserColumn,
    };
    treeBrowseData.vueList = TreeBrowseList.buildVue(
        {
            props: treeBrowseProps,
            container: treeBrowseContainer[0],
            isMobile: mobileAccess,
            events:
            {
                onRecordDoubleClick: function (rowData)
                {
                    if (isRelationshipField(rowData) && (!rowData.isOneToMany || oneToManyAllowed))
                    {
                        browseForward(rowData).done(function () { treeBrowseData.selectFirstRecord(); });
                    }
                    else
                    {
                        const { foundField } = treeBrowseData.vueList.selectRecord(rowData.fieldKey);
                        if (foundField)
                        {
                            treeBrowseData.treeBrowseList.trigger("treeBrowseSave", [treeBrowseData, rowData]);
                        }
                    }
                },
                onRecordSelected: function (rowData: FormattedFieldProps)
                {
                    treeBrowseTriggerBindingPoint.trigger("treeBrowseRowChange", [treeBrowseData, rowData]);
                }
            }
        });

    Object.assign(treeBrowseData.vueList, treeBrowseProps);

    let breadCrumbList = treeBrowse.find('.breadcrumbList');

    treeBrowseData.breadcrumbContainer = BreadcrumbContainer.buildVue(
        {
            isMobile: mobileAccess,
            container: breadCrumbList.get(0) as HTMLElement,
            props:
            {
                onBrowseBack: (index: number, crumb: ServerBreadcrumb) =>
                {
                    treeBrowseData.breadcrumbs.splice(index + 1, treeBrowseData.breadcrumbs.length);
                    breadcrumbsBrowseBack.call(crumb, treeBrowseData.absoluteTreeBrowseObjectPath);
                }
            }
        });
    treeBrowseData.breadcrumbContainer.breadcrumbs = breadCrumbList.data()["breadcrumbs"];

    treeBrowseData.parentSectionDiv = treeBrowse.find(".parentSectionDiv");

    treeBrowseData.fieldSearchBar = treeBrowse.find(".js-treebrowse__fieldSearch");
    treeBrowseData.fieldSearchButton = treeBrowseData.fieldSearchBar.find(".js-searchBoxButton");
    var fieldSearchInput = treeBrowse.find(".js-treebrowse__fieldSearchInput");
    fieldSearchInput.on("enter", function ()
    {
        treeBrowseData.fieldSearchButton.trigger("click");
    });

    browseForward().done(function (data)
    {
        // Callback for reporting
        treeBrowseTriggerBindingPoint.trigger("treeBrowseInitialize", [treeBrowseData, data]);

        var modalContent = treeBrowseData.treeBrowseList.closest('.content');
        modalContent.trigger('treeBrowseContentLoaded', [treeBrowseData, data]);
        treeBrowseData.selectRecord(data.seekFieldPath);
    });

    treeBrowseData.getExistingBreadcrumbIndex = function (path: String)
    {
        return treeBrowseData.breadcrumbs.findIndex((crumb) => crumb.positionObjectPath == path);
    };

    treeBrowseData.getCurrentBreadcrumb = function ()
    {
        return this.breadcrumbs[this.breadcrumbs.length - 1];
    };

    treeBrowseData.seekToField = function seekToField(rowData)
    {
        const rootModule = treeBrowseData.rootModule ?? treeBrowseData.lookupModule;
        const rootObject = treeBrowseData.rootObject ?? treeBrowseData.lookupObject;
        var fieldPathToParent = rowData.fieldPathToParent || rowData.positionObjectPath;
        var fieldSearcherData = treeBrowseData.fieldSearchButton.data();
        var absoluteTreeBrowseObjectPath = "";
        if (fieldSearcherData.absoluteTreeBrowseObjectPath)
        {
            absoluteTreeBrowseObjectPath = fieldSearcherData.absoluteTreeBrowseObjectPath;
        }

        treeBrowseData.replaceTreeBrowseData(
            rowData.moduleName, rowData.objectName, fieldPathToParent,
            null, null, rowData.breadcrumbData,
            rootModule, rootObject, absoluteTreeBrowseObjectPath,
            false, false)
            .done(function ()
            {
                if (treeBrowseData.selectRecord(rowData.fieldPath) && treeBrowseData.isDataField || treeBrowseData.isCalculatedField || treeBrowseData.isOneToMany)
                {
                    // Trigger a save event to add the selected record automagically
                    treeBrowseData.treeBrowseList.trigger("treeBrowseSave", [treeBrowseData, treeBrowseData.getSelectedFieldData()]);
                }
            });
    };

    treeBrowseData.selectRecord = function selectRecord(fieldKey)
    {
        let { foundField, rowData } = treeBrowseData.vueList.selectRecord(fieldKey);
        treeBrowseTriggerBindingPoint.trigger("treeBrowseRowChange", [treeBrowseData, rowData]);
        return foundField;
    };

    treeBrowseData.selectFirstRecord = function selectFirstRecord()
    {
        let { rowData } = treeBrowseData.vueList.selectFirstRecord();
        treeBrowseTriggerBindingPoint.trigger("treeBrowseRowChange", [treeBrowseData, rowData]);
    };

    treeBrowseData.clearSelectedRecord = function clearSelectedRecord()
    {
        treeBrowseData.vueList.clearSelectedRecord();
    };

    treeBrowseData.getSelectedFieldData = function ()
    {
        return treeBrowseData.vueList.getSelectedFieldData() as FormattedFieldProps | undefined;
    };

    treeBrowseData.getFieldData = function (field: JQueryInput)
    {
        return treeBrowseData.vueList.getFieldData($(field)[0]) as FormattedFieldProps | undefined;
    };

    treeBrowseData.getFieldElement = function (field: string)
    {
        return treeBrowseData.vueList.getFieldElement(field);
    };

    treeBrowseData.replaceTreeBrowseData = function (
        module: string, object: string, positionObjectPath: string,
        customUrl: string | null,
        _displayName: string | null,
        breadcrumbsLeadingBackToRoot: ServerBreadcrumb[],
        rootModule: string, rootObject: string, absoluteTreeBrowseObjectPath: string,
        updateParentSectionCombobox: boolean = true,
        replaceData: boolean = true)
    {
        const data = {
            positionObjectPath: positionObjectPath,
            lookupModule: module,
            lookupObject: object,
            controlName: controlName,
            insideOneToMany: insideOneToMany,
            routehash: routeHashData != null ? routeHashData.routehash : null,
            securitymodule: routeHashData != null ? routeHashData.securityModule : null,
            securityobject: routeHashData != null ? routeHashData.securityObject : null,
            securityscreen: routeHashData != null ? routeHashData.securityScreen : null,
            renderFields: treeBrowseData.renderFields,
            renderColumnGroups: treeBrowseData.renderColumnGroups && displayingCurrentOrChildSection(),
            renderStandardRelationships: treeBrowseData.renderStandardRelationships,
            renderOneToManyRelationships: treeBrowseData.renderOneToMany,
            rendersecuritydataobjectfieldsonly: treeBrowseData.renderSecurityDataObjectFieldsOnly,
            hideFieldsWithAnyRestriction: treeBrowseData.hideFieldsWithAnyRestriction,
            browseId: treeBrowseData.browseId,
            renderProxies: treeBrowseData.renderProxies,
            absoluteTreeBrowseObjectPath: absoluteTreeBrowseObjectPath,
            fieldsOnlyOfType: treeBrowseData.fieldsOnlyOfType,
            rootModuleName: rootModule,
            rootObjectName: rootObject,
            renderOperationFields: treeBrowseData.renderOperationFields,
            updateParentSectionComboBox: updateParentSectionCombobox,
            renderEnvironmentVariables: treeBrowseData.renderEnvironmentVariables,
            filterEnvironmentVariablesByLicense: treeBrowseData.filterEnvironmentVariablesByLicense,
            renderRowForImageReferences: treeBrowseData.renderRowForImageReferences,
            relationshipPath: convertPositionObjectPathToRelationshipPath(positionObjectPath),
            renderSuperUserColumn: treeBrowseData.renderSuperUserColumn,
            additionalFilterTypeName: treeBrowseData.additionalFilterTypeName,
        };

        return $.ajax(customUrl ? virtualDir + customUrl : fullExpandUrl,
            {
                type: "POST",
                dataType: "JSON",
                data: data,
            })
            .then(function (data)
            {
                if (replaceData)
                {
                    treeBrowseData.lookupModule = module;
                    treeBrowseData.lookupObject = object;
                    treeBrowseData.rootModule = rootModule;
                    treeBrowseData.rootObject = rootObject;
                    treeBrowseData.absoluteTreeBrowseObjectPath = absoluteTreeBrowseObjectPath;
                }

                treeBrowseData.breadcrumbs.splice(0, treeBrowseData.breadcrumbs.length, ...breadcrumbsLeadingBackToRoot);

                return replaceList(data);
            });
    };
    treeBrowseData.refresh = function ()
    {
        const lastBreadcrumb = treeBrowseData.getCurrentBreadcrumb();

        return treeBrowseData.replaceTreeBrowseData(
            lastBreadcrumb.moduleName,
            lastBreadcrumb.objectName,
            lastBreadcrumb.positionObjectPath,
            null,
            null,
            treeBrowseData.breadcrumbs,
            treeBrowseData.rootModule,
            treeBrowseData.rootObject,
            treeBrowseData.absoluteTreeBrowseObjectPath,
            false
        );
    }

    async function replaceList(data: ServerData)
    {
        // maxOneToManyNestedLevels == 0 means "unlimited depth"
        oneToManyAllowed = ((treeBrowseData.allowOneToManyBrowse == 'true' || treeBrowseData.allowOneToManyBrowse == true) &&
            (treeBrowseData.maxOneToManyNestedLevels == 0 || getCurrentDepth() < (treeBrowseData.maxOneToManyNestedLevels || 1)));

        const sections: TreeBrowseSection[] =
            [
                {
                    key: "fields",
                    rows: data.fields,
                    component: "dataField"
                },
                {
                    key: "relationships",
                    rows: data.relationships,
                    component: "relationshipField"
                },
                {
                    key: "envvars",
                    rows: data.environmentVariables,
                    component: "environmentVariableField",
                    container: data.environmentVariableContainer,
                    title: data.environmentVariableContainer ? undefined : "Environment Variables"
                },
                {
                    key: "special",
                    rows: data.specialFields,
                    component: "dataField",
                    container: data.specialFieldsContainer,
                    title: data.specialFieldsContainer ? undefined : "Special Fields"
                },
            ];
        treeBrowseData.vueList.sections = sections;
        if (!isMounted)
        {
            isMounted = true;
            treeBrowseData.treeBrowseList.html("").append(treeBrowseContainer.children());
        }

        treeBrowseData.mainContainer.scrollParent().scrollTop(0);

        if (data.parentSectionHTML)
        {
            await treeBrowseData.parentSectionDiv.viewpartHtml(data.parentSectionHTML);
            treeBrowse.find(".parentSectionComboBox").on("change", parentSectionComboboxBrowseBack);
        }

        treeBrowseData.standardFilterName = data.standardFilterName;
        treeBrowseData.standardFilterId = data.standardFilterId;// this should be renamed to standardFilterSkywardID

        // Ensure the portal has time to load
        return treeBrowseData.vueList.$nextTick().then(function ()
        {
            treeBrowseTriggerBindingPoint.trigger("treeBrowseLevelChanged", [treeBrowseData]);

            if (data.selector)
            {
                notifyDOMLoad(data.selector);
            }
            else
            {
                notifyDOMLoad(controlName + "_grid");
                treeBrowseData.treeBrowseList.closest('.content').trigger('treeBrowseContentLoaded');
            }
        });

        function getCurrentDepth()
        {
            // @TODO Improved in 2432607 but still need a better solution.  Keep track of current depth in data
            return treeBrowseData.breadcrumbs.length - 1;
        }
    }

    function displayingCurrentOrChildSection()
    {
        var parentSectionComboBox = treeBrowse.find('.parentSectionComboBox');
        if (parentSectionComboBox.length > 0)
        {
            //only render column groups when displaying the current section or children, not a parent section.
            return parentSectionComboBox.find(':selected').index() == parentSectionComboBox.find('option').length - 1;
        }
        // If we haven't rendered the combobox yet, we're in the process of reparenting the treebrowse to a new section, and will be viewing that section's root, not a parent.
        return true;
    }

    function parentComboBoxExtendTreeBrowse(clickedElement: JQuery, fieldPath: string)
    {
        let existingBreadcrumbIndex = treeBrowseData.getExistingBreadcrumbIndex(fieldPath);
        if (existingBreadcrumbIndex >= 0)
        {
            treeBrowseData.breadcrumbs = treeBrowseData.breadcrumbs.slice(0, existingBreadcrumbIndex + 1);
        }
        else
        {
            getAllPrecedingFieldPaths(fieldPath).forEach(function (path)
            {
                if (treeBrowseData.getExistingBreadcrumbIndex(path) === -1)
                {
                    var comboBoxOptionData = clickedElement.find('[data-field-path="' + path + '"]').data();
                    if (comboBoxOptionData)
                    {
                        treeBrowseData.breadcrumbs.push({
                            moduleName: comboBoxOptionData.moduleName,
                            objectName: comboBoxOptionData.objectName,
                            displayName: comboBoxOptionData.displayName,
                            isOneToMany: comboBoxOptionData.isOneToMany,
                            positionObjectPath: path
                        });
                    }
                }
            });
        }
    };

    function getAllPrecedingFieldPaths(fieldPath: string)
    {
        let paths = fieldPath.split('.');
        return paths.map(function (_, i)
        {
            return paths.slice(0, i + 1).join('.');
        });
    }

    function browseForward(targetData?: FormattedRelationshipField | undefined)
    {
        const firstParentComboBoxOptionData: JQuery.PlainObject = treeBrowse.find('.parentSectionComboBox > option:first-child').first().data();
        const rootModuleName: string = firstParentComboBoxOptionData?.moduleName || treeBrowseData.rootModule || treeBrowseData.lookupModule;
        const rootObjectName: string = firstParentComboBoxOptionData?.objectName || treeBrowseData.rootObject || treeBrowseData.lookupObject;
        let moduleName: string;
        let objectName: string;
        let fieldName: string;
        let positionObjectPath: string | undefined;
        let isOneToMany = false;
        let absoluteTreeBrowseObjectPath: string;
        let relationshipPath = null;

        const firstLoad = (!targetData); // First load passes no element

        if (firstLoad)
        {
            moduleName = treeBrowseData.seekModule || treeBrowseData.lookupModule;
            objectName = treeBrowseData.seekObject || treeBrowseData.lookupObject;
            if (treeBrowseData.seekPath)
            {
                positionObjectPath = treeBrowseData.seekPath;
                if (positionObjectPath[positionObjectPath.length - 1] === ".")
                {
                    positionObjectPath = positionObjectPath.substring(0, positionObjectPath.length - 1);
                }
            }
            absoluteTreeBrowseObjectPath = treeBrowseData.absoluteTreeBrowseObjectPath;
        }
        else
        {
            moduleName = targetData!.moduleName;
            objectName = targetData!.objectName;
            positionObjectPath = targetData!.fieldPath;
            fieldName = targetData!.relationshipName;
            isOneToMany = targetData!.isOneToMany;
            insideOneToMany = insideOneToMany || isOneToMany;
            absoluteTreeBrowseObjectPath = targetData!.absoluteTreeBrowseObjectPath;
            relationshipPath = targetData!.relationshipPath;
        }

        return $.ajax(fullExpandUrl,
            {
                type: "POST",
                dataType: "JSON",
                data:
                {
                    positionObjectPath: positionObjectPath,
                    lookupModule: moduleName,
                    lookupObject: objectName,
                    controlName: controlName,
                    insideOneToMany: insideOneToMany,
                    routehash: routeHashData != null ? routeHashData.routehash : null,
                    securitymodule: routeHashData != null ? routeHashData.securityModule : null,
                    securityobject: routeHashData != null ? routeHashData.securityObject : null,
                    securityscreen: routeHashData != null ? routeHashData.securityScreen : null,
                    renderFields: treeBrowseData.renderFields,
                    renderColumnGroups: treeBrowseData.renderColumnGroups === false ? false : displayingCurrentOrChildSection(), // Note: this parameter is only honored by a controller that accepts the posted value and merges it into its TreeBrowse data
                    renderStandardRelationships: treeBrowseData.renderStandardRelationships,
                    renderOneToManyRelationships: treeBrowseData.renderOneToMany,
                    rendersecuritydataobjectfieldsonly: treeBrowseData.renderSecurityDataObjectFieldsOnly,
                    hideFieldsWithAnyRestriction: treeBrowseData.hideFieldsWithAnyRestriction,
                    browseId: treeBrowseData.browseId,
                    renderProxies: treeBrowseData.renderProxies,
                    absoluteTreeBrowseObjectPath: absoluteTreeBrowseObjectPath,
                    fieldsOnlyOfType: treeBrowseData.fieldsOnlyOfType,
                    rootModuleName: rootModuleName,
                    rootObjectName: rootObjectName,
                    renderOperationFields: treeBrowseData.renderOperationFields,
                    updateParentSectionComboBox: treeBrowseData.parentSectionDiv.html() === "",
                    renderEnvironmentVariables: treeBrowseData.renderEnvironmentVariables,
                    filterEnvironmentVariablesByLicense: treeBrowseData.filterEnvironmentVariablesByLicense,
                    renderRowForImageReferences: treeBrowseData.renderRowForImageReferences,
                    relationshipPath: relationshipPath,
                    renderSuperUserColumn: treeBrowseData.renderSuperUserColumn,
                    additionalFilterTypeName: treeBrowseData.additionalFilterTypeName,
                }
            })
            .then(function (data)
            {
                if (!firstLoad)
                {
                    treeBrowseData.breadcrumbs.push({
                        displayName: fieldName,
                        isOneToMany: isOneToMany,
                        moduleName: moduleName,
                        objectName: objectName,
                        positionObjectPath: positionObjectPath ?? ''
                    });
                }
                else
                {
                    data.seekFieldPath = treeBrowseData.seekFieldPath;
                }

                return replaceList(data).then(function ()
                {
                    return data;
                });
            });
    }

    function parentSectionComboboxBrowseBack(this: HTMLElement)
    {
        let clickedElement = $(this);
        let targetDataStore = $(clickedElement[0]).find(':selected');

        let targetData = targetDataStore.data() as BreadcrumbData;
        let relationshipPath = convertPositionObjectPathToRelationshipPath(targetData.fieldPath);

        let parentSectionComboboxCallback = function ()
        {
            if (clickedElement.data().reparentTreebrowseOnChange === "True")
            {
                treeBrowseData.breadcrumbs.splice(0, treeBrowseData.breadcrumbs.length, {
                    moduleName: targetData.moduleName,
                    objectName: targetData.objectName,
                    isOneToMany: targetData.relationshipData?.relationshipType === "OneToMany" ? true : false,
                    displayName: targetData.displayName,
                    positionObjectPath: targetData.fieldPath
                });

                // Reparenting the treebrowse with the parent section combo box is a change to the core data
                // so some information needs to be updated in treeBrowseData
                treeBrowseData.absoluteTreeBrowseObjectPath = targetData.absoluteTreeBrowseObjectPath;

                setBrowseTarget(
                    targetData.moduleName,
                    targetData.objectName,
                    targetData.fieldPath,
                    controlName,
                    null,
                    targetData.displayName,
                    false,
                    treeBrowseData.rootModule,
                    treeBrowseData.rootObject,
                    treeBrowseData.absoluteTreeBrowseObjectPath,
                    false);
            }
            else
            {
                parentComboBoxExtendTreeBrowse(clickedElement, targetData.fieldPath);
            }
        };

        browseBack(targetData.moduleName, targetData.objectName, targetData.fieldPath, targetData.absoluteTreeBrowseObjectPath ?? "",
            relationshipPath, parentSectionComboboxCallback);
    }

    function breadcrumbsBrowseBack(this: ServerBreadcrumb, absoluteTreeBrowseObjectPath: string)
    {
        const relationshipPath = convertPositionObjectPathToRelationshipPath(this.positionObjectPath);
        return browseBack(this.moduleName, this.objectName, this.positionObjectPath, absoluteTreeBrowseObjectPath, relationshipPath, null);
    }

    function browseBack(module: string, object: string, positionObjectPath: string, absoluteTreeBrowseObjectPath: string,
        relationshipPath: string, additionalCallback: Function | null)
    {
        const firstParentComboBoxOptionData: JQuery.PlainObject = treeBrowse.find('.parentSectionComboBox > option:first-child').first().data();
        const rootModuleName = firstParentComboBoxOptionData?.moduleName || treeBrowseData.rootModule || treeBrowseData.lookupModule;
        const rootObjectName = firstParentComboBoxOptionData?.objectName || treeBrowseData.rootObject || treeBrowseData.lookupObject;

        let treeDataPromise = $.ajax(fullExpandUrl,
            {
                type: "POST",
                dataType: "JSON",
                data:
                {
                    positionObjectPath: positionObjectPath,
                    lookupModule: module,
                    lookupObject: object,
                    controlName: controlName,
                    insideOneToMany: insideOneToMany,
                    routehash: routeHashData != null ? routeHashData.routehash : null,
                    securitymodule: routeHashData != null ? routeHashData.securityModule : null,
                    securityobject: routeHashData != null ? routeHashData.securityObject : null,
                    securityscreen: routeHashData != null ? routeHashData.securityScreen : null,
                    renderFields: treeBrowseData.renderFields,
                    renderColumnGroups: treeBrowseData.renderColumnGroups && displayingCurrentOrChildSection(), // Note: this parameter is only honored by a controller that accepts the posted value and merges it into its TreeBrowse data
                    renderStandardRelationships: treeBrowseData.renderStandardRelationships,
                    renderOneToManyRelationships: treeBrowseData.renderOneToMany,
                    rendersecuritydataobjectfieldsonly: treeBrowseData.renderSecurityDataObjectFieldsOnly,
                    hideFieldsWithAnyRestriction: treeBrowseData.hideFieldsWithAnyRestriction,
                    browseId: treeBrowseData.browseId,
                    renderProxies: treeBrowseData.renderProxies,
                    absoluteTreeBrowseObjectPath: absoluteTreeBrowseObjectPath,
                    fieldsOnlyOfType: treeBrowseData.fieldsOnlyOfType,
                    rootModuleName: rootModuleName,
                    rootObjectName: rootObjectName,
                    renderOperationFields: treeBrowseData.renderOperationFields,
                    updateParentSectionComboBox: treeBrowseData.parentSectionDiv.html() === "",
                    renderEnvironmentVariables: treeBrowseData.renderEnvironmentVariables,
                    filterEnvironmentVariablesByLicense: treeBrowseData.filterEnvironmentVariablesByLicense,
                    renderRowForImageReferences: treeBrowseData.renderRowForImageReferences,
                    relationshipPath: relationshipPath,
                    renderSuperUserColumn: treeBrowseData.renderSuperUserColumn,
                    additionalFilterTypeName: treeBrowseData.additionalFilterTypeName,
                }
            });

        if (additionalCallback)
        {
            treeDataPromise.then(additionalCallback());
        }

        return treeDataPromise
            .then((data) =>
            {
                return replaceList(data);
            })
            .then(() =>
            {
                treeBrowseData.selectFirstRecord();
            });
    }
}

function convertPositionObjectPathToRelationshipPath(positionObjectPath: string): string
{
    return positionObjectPath?.split(".").slice(1).join(".");
}

function getRouteHashData()
{
    var currentPopupId = getCurrentPopupId();
    if (currentPopupId > 0)
    {
        var filterBuilderDialog = skyward.filterbuilder.dialogs.getCurrent();
        if (filterBuilderDialog)
        {
            var filterBuilderData = filterBuilderDialog.content.find(".filterBuilder").data();
            return {
                routehash: filterBuilderData.routehash,
                securityScreen: filterBuilderData.securityscreen,
                securityObject: filterBuilderData.securityobject,
                securityModule: filterBuilderData.securitymodule
            };
        }
    }
    return null;
}

function editSuperUserFieldVisibility(this: Element, _event: MouseEvent)
{
    const parentTreeBrowse = $(this).closest(".treeBrowse") as JQuery;
    const parentTreeBrowseData = getTreeBrowseData(parentTreeBrowse) as TreeBrowseData;
    const data = rawData(parentTreeBrowse);
    let treeBrowseData: TreeBrowseData;
    const dialog = showDialog("Edit Super User Field Visibility", $.ajax({
        url: virtualDir + "Common/TreeBrowse/GetPopup",
        method: "POST",
        type: "viewparthtml",
        data:
        {
            ...data,
            renderSuperUserColumn: true,
        }
    }),
        [
            {
                text: ButtonText.Save,
                icon: ButtonIcon.Save,
                click: () =>
                {
                    commitBFPChanges(dialog)
                        .then(() =>
                        {
                            return parentTreeBrowseData.refresh();
                        });
                }
            },
            closeButton
        ],
        {}
    );
    dialog.content.one("treeBrowseInitialize", (_event, newTreeBrowseData: TreeBrowseData) =>
    {
        treeBrowseData = newTreeBrowseData;

        const lastBreadcrumb = parentTreeBrowseData.getCurrentBreadcrumb();
        treeBrowseData.replaceTreeBrowseData(
            lastBreadcrumb.moduleName,
            lastBreadcrumb.objectName,
            lastBreadcrumb.positionObjectPath,
            null,
            null,
            $.extend(true, [], parentTreeBrowseData.breadcrumbs),
            parentTreeBrowseData.rootModule,
            parentTreeBrowseData.rootObject,
            parentTreeBrowseData.absoluteTreeBrowseObjectPath,
        );
    });
    dialog.content.on("treeBrowseSave", (_event, treeBrowseData: TreeBrowseData, _selectedField: FormattedFieldProps | undefined) =>
    {
        const dataField = treeBrowseData.vueList.selectedField as DataFieldProps | undefined;
        if (dataField && typeof dataField.showEye === "boolean")
        {
            treeBrowseData.vueList.modifyField(treeBrowseData.vueList.selectedFieldKey!,
                {
                    ...dataField,
                    showEye: !dataField.showEye,
                });
        }
    });

    function commitBFPChanges(dialog: ModalDialog)
    {
        return $.ajax(
            {
                url: virtualDir + "Security/BrowseFieldPath/MassUpdate",
                type: "POST",
                data: {
                    browseID: data?.browseId,
                    updates: JSON.stringify(Object.fromEntries(
                        Array.from(treeBrowseData.vueList.modifiedFields.entries())
                            .map(([_k, v]) => [(v as any as DataFieldProps).absoluteFieldPath, (v as any as DataFieldProps).showEye])
                    )),
                    preSaveWarningHash: dialog.container.data('preSaveWarningHash') || ""
                },
                success: function (data)
                {
                    if (data)
                    {
                        displayValidationMessages(data, dialog.content.find('.treeBrowseContainer'), $('#baseTreeBrowseErrorContainer'), true);
                        dialog.container.data('preSaveWarningHash', data.preSaveWarningHash);
                        dialog.content.scrollTop(0);
                    }
                    else
                    {
                        dialog.container.removeData('preSaveWarningHash');
                        dialog.close();
                    }
                }
            }
        );
    }
}
